import i18n from 'i18next';
import { I18nextProvider } from 'react-i18next';
import ChainedBackend from 'i18next-chained-backend';
import React from 'react';

export const defaultLocale = 'en';

export const LocalizationProvider = ({ children }: { children: React.ReactNode }) => {
  i18n
    .use(ChainedBackend)
    .init({
      lng: defaultLocale,
      fallbackLng: defaultLocale,
      interpolation: {
        escapeValue: false,
      },
      resources: {
        en: {
          translation: {
            'error.branch.duplicate': 'Branch with the same name already exists.',
            'error.branch.invalid.name': 'Invalid name for branch. Branch names must contain only letters, numbers, spaces, dashes, and underscores.',
            'error.branch.parent.missing': 'Parent branch does not exist.',
            'error.branch.headcommitcheckfailed': 'Branch you are working on has been modified externally. Please refresh the page.',
            'error.tag.duplicate': 'Tag already exists.',
            'error.tag.no.content.to.tag': 'Branch cannot be tagged as there is no content in the ruleset.',
            'error.tag.invalid.branch': 'Branch to be tagged does not exist.',
            'error.default': 'This action cannot be completed. Please contact underwriteme if the issue persists.',
            'error.branch.missing': 'Missing branch.',
            'error.merge.commit.not.found': 'Commit for merge not found.',
            'error.conflict.wrapup.line.order': 'There is a conflict with the wrap up lines execution order and this branch cannot be merged. Please resolve this manually.',
            'error.conflict.unmergeable': 'There are conflicts which cannot be resolved. Please resolve manually.',
            'error.conflict.invalid.resolutions': 'Resolutions provided for this merge is invalid. Check resolutions.',
            'error.merge.out.of.date.head.commit': 'Please pull changes first, source branch is changed recently.',
            'error.branch.protected.branch.violation': 'A violation has occurred when trying to update this protected branch : {{errorMessage}}',
            'push-validation-errors': 'There are validation errors in this ruleset. Please resolve before pushing to a public branch.',
            'error.validation.export': 'There are validation errors in this ruleset. Please resolve before exporting.',
            'error.validation.import': 'There are validation errors in this ruleset. Please resolve before importing.',
            'error.validation.line.test': 'There are validation errors in this ruleset. Please resolve before running line test.',
            'error.not.found.line.test': 'There was an error while running this test, either the file or test could not be found.',
            'error.not.saved.changes.line.test': 'You have unsaved changes. Please save your changes before running the unit test',
            'error.not.supplied.upload.file.line.test': 'Sheet required',
            'error.merge.merge.conflicts': 'Merge conflicts detected. Please resolve them first.',
            'validation.error.locale.already.exists': 'Locale with given name already exists.',
            'validation.error.locale.invalid.name': 'Names must start with a capital letter and may contain only letters, numbers and underscores.',
            'validation.error.bucket.missing': 'Bucket does not exist',
            'validation.error.question.already.exists': 'Question with given name already exists.',
            'validation.error.question.invalid.name': 'Names must start with a capital letter and may contain only letters, numbers and underscores.',
            'validation.error.question.missing': 'Question does not exist',
            'validation.error.enquiryLine.missing': 'Enquiry line does not exist',
            'validation.error.optionList.already.exists': 'An asset with this name already exists. All names must be unique',
            'validation.error.optionList.invalid.name': 'Option list names must start with a capital letter and may contain only letters, numbers and underscores.',
            'validation.error.optionList.option.invalid.name': 'Failed to add Option \'{{optionName}}\' to Option List \'{{optionListName}}\' - \'{{optionName}}\' is an invalid name. Names must start with a capital letter and may contain only letters, numbers and underscores.',
            'validation.error.optionList.option.duplicate.order': 'Option \'{{optionName}}\' has duplicate order.',
            'validation.error.optionList.option.duplicate.name': 'Option \'{{optionName}}\' already exists. All Option names in a list must be unique.',
            'validation.error.optionList.option.missing.default.locale': 'Option \'{{optionName}}\' has no text for the default locale.',

            'validation.error.excel.panel.header': 'Errors in spreadsheet',
            'validation.error.excel.optionList.header.required': '\'{{name}}\' column is missing',

            'validation.error.excel.optionList.already.exists': 'An asset with this name already exists. All names must be unique',
            'validation.error.excel.optionList.option.name.duplicate': '<b>{{column}}{{row}}</b> \'{{name}}\' Option name is already used',
            'validation.error.excel.optionList.option.name.required': '<b>{{column}}{{row}}</b> Option name is missing',
            'validation.error.excel.optionList.option.name.invalid': '<b>{{column}}{{row}}</b> \'{{name}}\' is an invalid option name. Names must start with a capital letter and may contain only letters, numbers and underscores.',
            'validation.error.excel.optionList.option.defaultLocale.required': '<b>{{column}}{{row}}</b> \'{{name}}\' Option must have a text value for the default locale',
            'validation.error.excel.optionList.invalid.cell': '<b>{{column}}{{row}}</b> Cell has invalid content!',

            'validation.locale.earliest.date': 'The earliest date allowed for this question is $lowerBound.',
            'validation.locale.latest.date': 'The latest date allowed for this question is $upperBound.',
            'validation.locale.future.date': 'Please enter a date in the future.',
            'validation.locale.past.date': 'Please enter a date in the past.',
            'validation.locale.invalid.date': 'Please enter a valid date.',
            'validation.locale.minimum.number': 'The minimum answer allowed for this question is $lowerBound.',
            'validation.locale.maximum.number': 'The maximum answer allowed for this question is $upperBound.',
            'validation.locale.whole.number': 'Please enter a whole number.',
            'validation.locale.invalid.number': 'Please enter a number.',
            'validation.locale.invalid.option': '$answer is not a valid answer for this question.',
            'validation.locale.maximum.characters': 'The maximum number of characters is $maxLength.',
            'validation.error.table.invalid.name': 'Invalid name for table (this is the name of the excel file without the extension). Names must start with a capital letter and may contain only letters, numbers and underscores.',
            'validation.error.asset.already.exists': 'An asset given name already exists.',
            'validation.error.readme.too.long': 'Readme content cannot exceed 10000 characters',
            'validation.error.import': 'Definition being imported has validation errors. Please import into a private branch and edit the rules to fix the issue.',
            'validation.error.asset.line.tests.not.exists': 'Line Test does not exist',
            'validation.error.asset.line.tests.spreadsheet.errors': 'Line Test Spreadsheet contains errors, please fix issues shown in red on the downloaded spreadsheet.',
            'validation.error.asset.line.not.exists': 'Enquiry Line does not exist',

            'validation.error.excel.invalid.cell': 'Cell at [{{column}},{{row}}] in sheet \'{{sheet}}\' has invalid content!',

            // FormModal Common
            'form.modal.submit.label': 'Submit',
            'form.modal.cancel.label': 'Cancel',

            // Grid filters
            'grid.filter.button.reset': 'Reset',
            'grid.filter.button.search': 'Search',
            'grid.filter.button.clearFilters': 'Clear Filters',
            'grid.filter.text.placeholder': 'Search',
            'grid.filter.tooltip': 'Filter by {{columnName}}',
            'grid.date.filter.operation.at': 'At',
            'grid.date.filter.operation.before': 'Before',
            'grid.date.filter.operation.after': 'After',
            'grid.date.filter.operation.between': 'Between',

            // user management
            'validation.error.user.email.must.be.email': 'Email must be a valid email address.',
            'validation.error.user.email.in_use': 'There is already a user with the same email address.',
            'validation.error.user.role.invalid': 'A role has been selected that cannot be applied to this user: {{errorMessage}}',
            'validation.error.user.duplicate.profiles': 'This user has been corrupted with multiple application profiles. Please contact Support to rectify the issue.',
            'resetpassword.modal.title': 'Reset password',
            'resetpassword.modal.success': 'Email sent.',
            'resetpassword.modal.text': 'An email will be sent to reset this password.',

            'usermanagement.create.modal.title': 'Create new user',
            'usermanagement.create.modal.email.label': 'Username:',
            'usermanagement.create.modal.email.placeholder': 'Must be valid email address',
            'usermanagement.create.modal.email.validation.text': 'Please input an email address',
            'usermanagement.create.modal.languagetag.label': 'Language Tag:',
            'usermanagement.create.modal.languagetag.validation.text': 'Please select a language',
            'usermanagement.create.modal.roles.label': 'Roles:',
            'usermanagement.create.modal.roles.validation.text': 'Please select some roles',
            'usermanagement.create.modal.success': 'User {{createdUsername}} created',

            'usermanagement.update.modal.title': 'Update user',
            'usermanagement.update.modal.email.label': 'Username:',
            'usermanagement.update.modal.email.placeholder': 'Must be valid email address',
            'usermanagement.update.modal.languagetag.label': 'Language Tag:',
            'usermanagement.update.modal.languagetag.validation.text': 'Please select a language',
            'usermanagement.update.modal.roles.label': 'Roles:',
            'usermanagement.update.modal.roles.validation.text': 'Please select some roles',
            'usermanagement.update.modal.warning.message': 'User {{updatedUsername}} updated.',
            'usermanagement.update.modal.warning.description': 'This user will need to log out and log in again to activate any changes to user profiles.',

            'usernamagement.delete.user': 'User Management Delete User',
            'usermanagement.delete.modal.title': 'Delete User',
            'usermanagement.delete.modal.text': 'Permanently delete the user <b>{{user}}</b>',
            'usermanagement.delete.modal.success': 'User deleted.',
            'usermanagement.delete.modal.error': 'Delete failed due to an error.',
            'usermanagement.delete.button.ok': 'Delete',
            'usermanagement.delete.button.cancel': 'Cancel',


            'usermanagement.grid.column.username.title': 'Username',
            'usermanagement.grid.column.languagetag.title': 'Language Tag',
            'usermanagement.grid.column.roles.title': 'Roles',

            // consent form
            'consent.form.tracking.title': 'Update Consent',
            'consent.form.tracking.enable.label': 'Enable tracking',
            'consent.form.tracking.button.save.text': 'Save',
            'consent.form.tracking.modal.title': 'Save Changes',
            'consent.form.tracking.modal.success': 'Consent updated.',
            'consent.form.tracking.modal.content': 'Are you sure you want to update the consent?',

            // todo add enquiry line based errors here

            // Upload
            'error.upload.xlsx.format.not.valid': 'Cannot import file as it is not a valid export in xlsx or xls format',
            'error.upload.xlsx.foreach.validation': 'Cannot import foreach file as it has validation errors. Please use the foreach template and try again.',
            'error.upload.xlsx.foreach.file.invalid': 'Cannot import file as it is not a valid foreach file. Please use the foreach template and try again.',
            'error.upload.xlsx.foreach.max.gen.exceeded': 'Cannot import file as the number of generated test cases has exceeded the maximum limit set. Please reduce the potential number of test cases that can be generated and try again.',

            // Translations
            'translation.success': 'Translation imported successfully',
            'warn.translation.unrecognised.assets': 'Your import file contains assets that do not exist in your private branch. These additional assets have not been imported.',
            'warn.translation.unrecognised.locales': 'Your import file contains locales that do not exist in your private branch. These additional locales have not been imported.',
            'warn.translation.unrecognised.locales.and.assets': 'Your import file contains locales and assets that do not exist in your private branch. These additional locales and assets have not been imported.',

            // RALT
            'line.tests.run.button': 'Run tests',
            'line.tests.run-all-tests.button.label': 'Run Tests',
            'line.tests.run.modal.title': 'Run tests',
            'line.tests.run.modal.branch.label': 'Private Branch:',
            'line.tests.run.modal.branch.validation.warn': 'Please select a branch',
            'line.tests.run.success': 'We are currently processing all line tests for the branch {{branchName}}.<p></p>This will take several minutes to process. Please check the main page for the current status of the reports.',
            'line.tests.export.modal.title': 'Export test summary',
            'line.tests.export.modal.content': 'Results will be exported as an Excel file.<p></p>This operation might take a while for rule sets with a large set of tests, so please do not close or refresh the browser.',
            'line.tests.grid.column.name.title': 'Name',
            'line.tests.grid.column.date.title': 'Date',
            'line.tests.grid.column.branch.title': 'Branch',
            'line.tests.grid.column.user.title': 'User',
            'line.tests.grid.column.summary.title': 'Summary',
            'line.tests.grid.column.test.result.title': 'Test Result',
            'line.tests.grid.column.status.title': 'Status',
            'line.tests.grid.column.export.title': 'Export',
            'line.tests.grid.export.tooltip.title': 'Export run results',
            'line.tests.grid.summary.text.passed': 'All tests have passed.',
            'line.tests.grid.summary.text.failed': 'There are errors. Please download the report to view the failed tests',
            'line.tests.grid.summary.text.running': 'In Progress...',
            'line.tests.grid.summary.text.no.tests': 'There are no tests to run.',
            'line.tests.grid.summary.text.invalid.status': 'Invalid status. Please contact underwriteme if the issue persists.',
            'line.tests.grid.status.running.label': 'Running...',
            'line.tests.grid.status.passed.label': 'Passed',
            'line.tests.grid.status.failed.label': 'Failed',
            'line.tests.grid.status.invalid.label': 'Invalid',
            'line.tests.grid.status.none.label': 'None',
            'line.tests.grid.status.completed.warning': '-WARNING',
            'line.tests.panel.title': 'Unit Tests',
            'line.tests.template.download': 'Template',
            'line.tests.template.button.blank': 'Blank',
            'line.tests.template.button.foreach': 'For Each',
            'line.tests.upload.button.label': 'Upload',
            'line.tests.upload.modal.title': 'Add Unit Test',
            'line.tests.upload.modal.excel.label': 'Sheet',
            'line.tests.upload.modal.forEach.label': 'For each',
            'line.tests.delete.modal.title': 'Delete Unit Test',
            'line.tests.delete.modal.content': 'Permanently delete the unit test <b>{{name}}</b>?',
            'line.tests.delete.modal.cancel.label': 'Cancel',
            'line.tests.delete.modal.ok.label': 'Delete',

            // History
            'history.grid.column.date.title': 'Date',
            'history.grid.column.user.title': 'User',
            'history.grid.column.message.title': 'Message',
            'history.grid.column.tags.title': 'Tags',
            'history.grid.column.export.title': 'Export',
            'history.grid.export.button.tooltip': 'Export this commit',
            'history.grid.export.modal.title': 'Export Commit',
            'history.grid.export.modal.content': 'Rules for selected commit will be exported as a zip file. You can use this zip file as an import.<p></p>This operation might take a while for large rulesets.',

            // Commit Summary
            'history.commit.summary.grid.column.asset.action.title': 'Action Type',
            'history.commit.summary.grid.column.asset.type.title': 'Asset Type',
            'history.commit.summary.grid.column.asset.name.title': 'Asset Name',
            'history.commit.summary.grid.column.user.action.title': 'User Action',

            // Deployments
            'deployment.create.tag.button': 'New Tag',
            'deployment.create.tag.modal.title': 'New Tag',
            'deployment.create.tag.modal.name.label': 'Tag Name:',
            'deployment.create.tag.modal.name.warn': 'Please input a name',
            'deployment.create.tag.modal.success': 'Tag {{tagName}} created.',
            'deployment.export.tag.modal.title': 'Export Tag',
            'deployment.export.tag.modal.content': 'Rules for selected tag will be exported as a zip file. You can use this zip file as an import.<p></p>This operation might take a while for large rulesets.',
            'deployment.grid.column.export.title': 'Export',
            'deployment.grid.export.button.tooltip': 'Export this tag',
            'deployment.grid.column.tag.title': 'Tag',
            'deployment.grid.column.actions.title': 'Actions',
            'deployment.grid.tag.link.tooltip': 'View in Editor',
            'deployment.grid.column.created.title': 'Created',
            'deployment.history.modal.title': 'Deployment History',
            'deployment.history.grid.column.date.title': 'Date',
            'deployment.history.grid.column.user.title': 'User',
            'deployment.history.grid.column.action.title': 'Action',
            'deployment.history.grid.column.action.disabled.tooltip': 'You are not authorised to perform this deployment. Re-login if your account was updated recently.',
            'deployment.update.state.modal.title': 'Update Deployment State',
            'deployment.update.state.modal.content': 'Are you sure you wish to <b>{{status}}</b> this tag?',
            'deployment.update.state.modal.content.void': 'void',
            'deployment.update.state.modal.content.deploy': 'deploy',
            'deployment.update.state.modal.success': 'Success.',
            'deployment.status.live': 'LIVE',
            'deployment.status.pending': 'PENDING',
            'deployment.status.voided': 'VOIDED',

            // Editor
            // Navigation
            'editor.navigation.readOnly': 'Read-only',
            'editor.navigation.tag.date': 'Creation Date:',
            'editor.navigation.delete.button.cancel': 'Cancel',
            'editor.navigation.delete.button.ok': 'Delete',
            'editor.navigation.category.enquiryLines': 'Enquiry Lines',
            'editor.navigation.createTitle.enquiryLine': 'Enquiry Line',
            'editor.navigation.delete.enquiryLine.title': 'Delete Enquiry Line',
            'editor.navigation.delete.enquiryLine.content': 'Permanently delete the enquiry line <b>{{name}}</b>?',
            'editor.navigation.category.wrapUpLines': 'Wrap-up Lines',
            'editor.navigation.createTitle.wrapUpLine': 'Wrap-up Line',
            'editor.navigation.delete.wrapUpLine.title': 'Delete Wrap-up Line',
            'editor.navigation.delete.wrapUpLine.content': 'Permanently delete the wrap-up line <b>{{name}}</b>?',
            'editor.navigation.category.rootEnquiryLines': 'Root Enquiry Lines',
            'editor.navigation.createTitle.rootEnquiryLine': 'Root Enquiry Line',
            'editor.navigation.delete.rootEnquiryLine.title': 'Delete Root Enquiry Line',
            'editor.navigation.delete.rootEnquiryLine.content': 'Permanently delete the root enquiry line <b>{{name}}</b>?',
            'editor.navigation.category.buckets': 'Buckets',
            'editor.navigation.createTitle.bucket': 'Bucket',
            'editor.navigation.delete.bucket.title': 'Delete Bucket',
            'editor.navigation.delete.bucket.content': 'Permanently delete the bucket <b>{{name}}</b>?',
            'editor.navigation.category.options': 'Options',
            'editor.navigation.createTitle.option': 'Option',
            'editor.navigation.delete.option.title': 'Delete Option',
            'editor.navigation.delete.option.content': 'Permanently delete the option <b>{{name}}</b>?',
            'editor.navigation.category.questions': 'Questions',
            'editor.navigation.createTitle.question': 'Question',
            'editor.navigation.delete.question.title': 'Delete Question',
            'editor.navigation.delete.question.content': 'Permanently delete the question <b>{{name}}</b>?',
            'editor.navigation.category.locales': 'Locales',
            'editor.navigation.createTitle.locale': 'Locale',
            'editor.navigation.category.priorEnquiryChannels': 'Prior Enquiry Channels',
            'editor.navigation.createTitle.priorEnquiryChannel': 'Prior Enquiry Channel',
            'editor.navigation.delete.priorEnquiryChannel.title': 'Delete Prior Enquiry Channel',
            'editor.navigation.delete.priorEnquiryChannel.content': 'Permanently delete the prior enquiry channel <b>{{name}}</b>?',
            // Navigation Search Bar
            'editor.navigation.search.placeholder': 'Search...',
            'editor.navigation.create.placeholder': 'Add a new asset',
            'editor.navigation.collapseAll': 'Collapse all',

            // Line
            'editor.line.wrapup.order.drawer.title': 'Wrap-up lines order',
            'editor.line.wrapup.order.button': 'Order',
            'editor.line.menu.unnamed.line.name': 'Unnamed Line',
            'editor.line.menu.search.tooltip.title': 'Graph Search',
            'editor.line.menu.search.popover.placeholder': 'Search...',
            'editor.line.menu.button.export': 'Export PDF',
            'editor.line.menu.button.questions': 'Questions',
            'editor.line.menu.button.tests': 'Unit Tests',
            'editor.line.menu.button.tables': 'Tables',
            'editor.line.menu.button.properties': 'Properties',
            'editor.line.menu.button.readme': 'Notes',
            'editor.line.properties.form.name.label': 'Name',
            'editor.line.properties.form.locale.label': 'Locale',
            'editor.line.properties.form.multiTriggered.label': 'Multiple Triggering',
            'editor.line.properties.form.startingNodes.label': 'Starting Nodes',
            'editor.line.properties.form.tags.label': 'Tags',
            'editor.line.properties.form.categories.label': 'Categories',
            'editor.line.properties.form.locale.displayName.label': 'Display Name',
            'editor.line.properties.form.locale.preamble.label': 'Preamble',
            'editor.line.properties.form.locale.section.label': 'Section',
            'editor.line.properties.alert.missing.locale': 'In order to view/edit locale based properties, you must create a locale first.',
            'editor.line.export.pdf.canvas.timeout.error': 'Timeout waiting for element: {{htmlElement}}. If this error persists, please contact support.',
            'editor.line.export.pdf.export.diagram.missing': 'Diagram is not available. If this error persists, please contact support.',

            // Questions Drawer
            'editor.line.questions.drawer.title': 'Available Questions',
            'editor.line.questions.drawer.grid.node.title': 'Node',
            'editor.line.questions.drawer.grid.name.title': 'Name',
            'editor.line.questions.drawer.grid.text.title': 'Question Text',
            'editor.line.questions.drawer.grid.answers.title': 'Answers',
            'editor.line.questions.drawer.grid.help.title': 'Help Text',
            'editor.line.questions.drawer.grid.type.title': 'Type',

            // Readme Drawer
            'editor.readme.drawer.delete.success': 'Notes deleted.',
            'editor.readme.drawer.create.submit.title': 'Add',
            'editor.readme.drawer.edit.submit.title': 'Submit',
            'editor.readme.drawer.edit.cancel.title': 'Cancel',
            'editor.readme.drawer.view.missing.label': 'This asset does not have any notes yet.',
            'editor.readme.drawer.view.addNotes.button.title': 'Add Notes',
            'editor.readme.drawer.modal.delete.title': 'Delete Notes',
            'editor.readme.drawer.modal.delete.confirm.text': 'Permanently delete the notes for <b>{{name}}</b>?',
            'editor.readme.drawer.modal.delete.button.cancel': 'Cancel',
            'editor.readme.drawer.modal.delete.button.ok': 'Delete',
            'editor.readme.drawer.content.too.long': 'Notes cannot be more than {{maxCharacters}} characters long.',

            // Modal
            // Save Commit
            'modal.save.changes.title': 'Save Changes',
            'modal.save.changes.comment.label': 'Comment:',
            'modal.save.changes.comment.placeholder': 'Optional commit message',

            // Link Modal
            'modal.link.title': 'Link',
            'modal.link.text.missing.message': 'Expression required',

            // Branch Node Modal
            'modal.branch.node.title': 'Branch Node',
            'modal.branch.node.asset.empty.error.message': 'Query asset required',
            'modal.branch.node.placeholder': 'Start typing for auto-complete',

            // Static Trigger Node Modal
            'modal.static.trigger.node.title': 'Rule Trigger Node - Static',
            'modal.static.trigger.node.line.label': 'Line:',
            'modal.static.trigger.node.line.empty.message': 'Line required',
            'modal.static.trigger.node.tag.label': 'Tag:',
            'modal.static.trigger.node.line.placeholder': 'Start typing for auto-complete',

            // Dynamic Trigger Node Modal
            'modal.dynamic.trigger.node.title': 'Rule Trigger Node - Dynamic',
            'modal.dynamic.trigger.node.question.label': 'Question:',
            'modal.dynamic.trigger.node.question.missing': 'Question required',
            'modal.dynamic.trigger.node.question.placeholder': 'Start typing for auto-complete',

            // Contributions Modal
            'modal.contributions.title': 'Contributions',
            'modal.contributions.bucket.label': 'Bucket',
            'modal.contributions.bucket.missing': 'Bucket required',
            'modal.contributions.value.label': 'Value',
            'modal.contributions.value.missing': 'Value required',
            'modal.contributions.button.add': 'Add contribution',

            // Table Modal
            'modal.table.title': 'Table',
            'modal.table.label': 'Table:',
            'modal.table.missing': 'Table required',

            // Create Public Branch Modal
            'modal.createPublicBranch.title': 'Create Public Branch',
            'modal.createPublicBranch.name.label': 'Public Branch Name',
            'modal.createPublicBranch.name.required': 'Please input a name',
            'modal.createPublicBranch.success': 'Public Branch {{branchName}} created',

            // Delete Public Branch Modal
            'modal.deletePublicBranch.title': 'Delete Public Branch',
            'modal.deletePublicBranch.content': 'Permanently delete the public branch <b>{{name}}</b>?<br />You will remove all rules and history, and will not be able to retrieve them.',
            'modal.deletePublicBranch.button.ok': 'Delete',
            'modal.deletePublicBranch.button.cancel': 'Cancel',

            // Create Private Branch Modal
            'modal.createPrivateBranch.title': 'New Private Branch',
            'modal.createPrivateBranch.name.label': 'Branch Name',
            'modal.createPrivateBranch.name.required': 'Please input a name',
            'modal.createPrivateBranch.success': 'Private branch {{branchName}} created',

            //Delete Private Branch Modal
            'modal.deletePrivateBranch.title': 'Delete Private Branch',
            'modal.deletePrivateBranch.content': 'Permanently delete the private branch <b>{{name}}</b>?',
            'modal.deletePrivateBranch.confirm.message': 'Private branch deleted',
            'modal.deletePrivateBranch.button.ok': 'Delete',
            'modal.deletePrivateBranch.button.cancel': 'Cancel',

            // Fork Public Branch Modal
            'modal.forkPublicBranch.title': 'Fork Public Branch',
            'modal.forkPublicBranch.name.label': 'Public Branch Name',
            'modal.forkPublicBranch.name.required': 'Please input a name',
            'modal.forkPublicBranch.success': 'Public Branch {{branchName}} forked',

            // Common
            'button.save': 'Save',
            'button.cancel': 'Cancel',
            'message.saved': 'Saved',

            // Assets
            // Option Lists
            'option.title.create': 'Creating option list',
            'option.title.edit': 'Editing option list',
            'option.save.button': 'Save',
            'option.import.button': 'Import',
            'option.export.button': 'Export',
            'option.label.name': 'Name',
            'option.modal.import.title': 'Import Options',
            'option.modal.import.label': 'Please select a file',
            'option.grid.column.order.title': 'Order',
            'option.grid.column.name.title': 'Name',
            'option.grid.column.text.title': 'Text ({{locale}})',
            'option.grid.column.triggers.title': 'Triggers',
            'option.grid.column.tags.title': 'Tags',
            'option.grid.column.categories.title': 'Categories',
            'option.search.placeholder': 'Search options...',
            'option.search.clear.button': 'Clear',

            // Locale
            'locale.label.name': 'Name',
            'locale.label.description': 'Description',
            'locale.label.language.code': 'Language Code',
            'locale.label.country.code': 'Country Code',
            'locale.label.fallback.locale': 'Fallback locale',
            'locale.label.date.format': 'Date format',
            'locale.label.missing.date.format': 'Please choose Date format',
            'locale.label.is.default': 'Is Default',
            'locale.label.earliest.date': 'Earliest date',
            'locale.label.latest.date': 'Latest date',
            'locale.label.future.date': 'Future date',
            'locale.label.past.date': 'Past date',
            'locale.label.invalid.date': 'Invalid date',
            'locale.label.minimum.number': 'Minimum number',
            'locale.label.maximum.number': 'Maximum number',
            'locale.label.whole.number': 'Whole number',
            'locale.label.invalid.number': 'Invalid number',
            'locale.label.invalid.option': 'Invalid option',
            'locale.label.maximum.characters': 'Maximum characters',

            // Table
            'table.warning.save.line.first.text': 'You need to save this enquiry line first to add any tables.',
            'table.warning.save.line.first.modal.close.button.text': 'OK',
            'table.delete.used.nodes.confirm.title': 'Please remove all usages of {{tableName}} in the graph before deleting it.',
            'table.delete.used.nodes.confirm.cancel': 'Cancel',
            'table.delete.used.nodes.confirm.ok': 'See usage',
            'table.delete.confirm.title': 'Delete Table',
            'table.delete.confirm.content': 'Permanently delete the table <b>{{tableName}}</b>?',
            'table.delete.confirm.cancel': 'Cancel',
            'table.delete.confirm.ok': 'Delete',
            'table.add.title': 'Add Table',
            'table.add.sheet.label': 'Sheet',
            'table.add.sheet.label.required': 'Sheet required',
            'table.add.sheetAxis.label': 'Sheet axis:',
            'table.list.tables': 'Tables',
            'table.list.tables.column.name.title': 'Name',
            'table.list.tables.column.sheetAxis.title': 'Sheet Axis',

            // Question
            'question.create.generic.title': 'Creating question under the Questions category',
            'question.create.line.title': 'Creating question under the {{line}} enquiry line',
            'question.update.generic.title': 'Editing question under the Questions category',
            'question.update.line.title': 'Editing question under the {{line}} enquiry line',
            'question.form.name.label': 'Name',
            'question.form.type.label': 'Type',
            'question.form.tags.label': 'Tags',
            'question.form.multivalued.label': 'Multi-valued',
            'question.form.isglobal.label': 'Is Global',
            'question.form.maxlength.label': 'Max length',
            'question.form.lowerbound.label': 'Lower bound',
            'question.form.upperbound.label': 'Upper bound',
            'question.form.roundingpreference.label': 'Rounding Preference',
            'question.form.optionlist.label': 'Option list',
            'question.form.includedcategories.label': 'Included categories',
            'question.form.excludedcategories.label': 'Excluded categories',
            'question.form.minsearchchars.label': 'Min search characters',
            'question.form.islenient.label': 'Is Lenient',
            'question.form.defaultoption.label': 'Default option',
            'question.form.locale.text.label': 'Text',
            'question.form.locale.helptext.label': 'Help text',
            'question.orphan.warning.label': 'The enquiry line this question belonged to has been removed. Please delete this question and recreate it in either the global questions section, or under a different enquiry line.',

            // Bucket
            'bucket.create.title': 'Creating bucket',
            'bucket.update.title': 'Editing bucket',
            'bucket.form.name.label': 'Name',
            'bucket.form.type.label': 'Type',
            'bucket.form.optionListName.label': 'Option List Name',
            'bucket.form.groupedBuckets.label': 'Grouped Buckets',
            'bucket.form.visibleWhen.label': 'Visible When',

            // Editor Validations
            'editor.validation.button': 'Validation',
            'editor.validation.panel.title': 'Validation',
            'editor.validation.panel.run.button': 'Run Validation',
            'editor.validation.panel.table.name': 'Name',
            'editor.validation.panel.table.problem': 'Problem',
            'editor.validation.panel.title.enquiryDefinition': 'Enquiry Definition',
            'editor.validation.panel.title.enquiryLines': 'Enquiry Lines',
            'editor.validation.panel.title.wrapUpLines': 'Wrap-up Lines',
            'editor.validation.panel.title.bucket': 'Buckets',
            'editor.validation.panel.title.optionLists': 'Option Lists',
            'editor.validation.panel.title.questions': 'Questions',
            'editor.validation.panel.title.tables': 'Tables',
            'editor.validation.panel.title.locales': 'Locales',
            'editor.validation.panel.title.priorEnquiries': 'Prior Enquiry Channels',

            // Enquiry History
            'history.enquiry.not.found': 'Enquiry with id \'{{enquiryId}}\' not found.',
            'history.lines.bucket.sum.label': 'sum',
            'history.lines.bucket.min.label': 'min',
            'history.lines.bucket.max.label': 'max',
            'history.lines.bucket.contributions.label': 'contributions',
            'history.lines.bucket.contributions.table.enquiryLines.label': 'Enquiry Lines',
            'history.lines.bucket.contributions.table.values.label': 'Values',
            'history.lines.debugInfo.title': 'Debug Info',

            // Enquiry Loader
            'history.enquiryLoader.environment.label': 'Environment',
            'history.enquiryLoader.enquiryId.label': 'Enquiry ID',
            'history.enquiryLoader.submit.button': 'Load',

            // Enquiry Details (Snapshots)
            'snapshots.enquiryLoader.title': 'Enquiry Loader',
            'snapshots.enquiryDetails.title': 'Enquiry Details',
            'snapshots.enquiryDetails.enquiryId.label': 'ID',
            'snapshots.enquiryDetails.branch.label': 'Branch',
            'snapshots.enquiryDetails.tag.label': 'Tag',
            'snapshots.enquiryDetails.locale.label': 'Locale',
            'snapshots.decisionSnapshots.title': 'Decision Snapshots',
            'snapshots.priorEnquiries.title': 'Prior Enquiries',
            'snapshots.subsequentEnquiries.title': 'Subsequent Enquiries',

            // Shell
            'shell.enquiry.not.found': 'Sorry, this Enquiry ID could not be found. <p></p> Please ensure that the ID is correct and that you are in the right environment. If the issue persists, please contact support.',

            // Not found page
            'notfound.page.title.text': "Sorry, we couldn't find that page",
            'notFound.page.subTitle.text': 'What you are looking for may have been renamed, misspelled or deleted.',
            'notFound.page.button.home.text': 'Home',

            // Unauthorised page
            'unauthorised.page.text': 'Sorry, you are not authorized to view this page. You can try logging out and logging in with a different user.',

            // Components
            // ContributionValues
            'contributionValue.duration': ' for ',

            // Details Component
            'details.enquiryLoader.title': 'Enquiry Loader',
            'details.enquiryDetails.title': 'Enquiry Details',
            'details.label.id': 'ID',
            'details.label.collation': 'Collation',
            'details.label.branch': 'Branch',
            'details.label.tag': 'Tag',
            'details.label.locale': 'Locale',
            'details.label.dateFormatPreference': 'Date Format Preference',
            'details.label.isSatisfied': 'isSatisfied',
            'details.label.isOpen': 'isOpen',
            'details.collation.breadthFirst': 'Breadth First Order',
            'details.collation.depthFirst': 'Depth First Order',
            'details.enquiryConsole.title': 'Enquiry Console',
            'details.tooltip.expandAll': 'Expand all',
            'details.tooltip.collapseAll': 'Collapse all',
            'details.tooltip.copyToClipboard': 'Copy to clipboard',
            'details.message.dataCopied': 'Data copied to clipboard',
            'details.value.true': 'true',
            'details.value.false': 'false',

            // Enquiry Loader Component
            'enquiryLoader.environment.label': 'Environment',
            'enquiryLoader.enquiryId.label': 'Enquiry ID',
            'enquiryLoader.branch.label': 'Branch',
            'enquiryLoader.tag.label': 'Tag',
            'enquiryLoader.locale.label': 'Locale',
            'enquiryLoader.loadEnquiry.button': 'Load',
            'enquiryLoader.startEnquiry.button': 'Start',
            'enquiryLoader.advancedStart.button': 'Advanced Start',

            // Text Editor Component
            'textEditor.toggle.toRichText': 'Switch to Rich Text',
            'textEditor.toggle.toPlainText': 'Switch to Plain Text',

            // Browser tab Titles
            'browser.tab.title.enquiry.editor': 'Enquiry Editor',
            'browser.tab.title.enquiry.history': 'Enquiry History',
            'browser.tab.title.enquiry.shell': 'Enquiry Shell',
            'browser.tab.title.user.management': 'User Management',
            'browser.tab.title.consent.management': 'Consent Management',
            'browser.tab.title.rules.editor': 'Rules Editor',
          },
        },
      },
    });

  return (
    <I18nextProvider i18n={i18n}>
      {children}
    </I18nextProvider>
  );
};
